import axios from 'axios';
import { clearAuthStorage, isLikelyJwtToken } from './authStorage';
import { getLoginRedirectPath } from './environmentUtils';
import { readCsrfToken } from './csrfToken';

/**
 * Normalize an environment-provided API base URL into an axios-ready `/api/` base.
 *
 * We accept:
 * - an origin like `https://example.com`
 * - a path like `https://example.com/api`
 *
 * and convert it to a consistent trailing-slash form: `https://example.com/api/`.
 *
 * @param {unknown} value
 * @returns {string|null}
 */
const normalizeApiBase = (value) => {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.replace(/\/+$/, '');
  if (!trimmed) return null;
  return trimmed.endsWith('/api') ? `${trimmed}/` : `${trimmed}/api/`;
};

// Prefer REACT_APP_API_URL so local scripts (e.g. `start:local`) can override .env defaults.
const API_BASE_URL =
  normalizeApiBase(process.env.REACT_APP_API_URL) ||
  normalizeApiBase(process.env.REACT_APP_API_BASE_URL) ||
  'https://speakable.online/api/';

// Stripe-only client target (for local Stripe testing).
const STRIPE_API_BASE_URL =
  normalizeApiBase(process.env.REACT_APP_STRIPE_API_URL) ||
  API_BASE_URL;

/**
 * Shared axios instance used across the frontend to call the backend API.
 *
 * Data flow:
 * - `baseURL` is computed once from env/defaults.
 * - `withCredentials` is enabled so the browser sends the httpOnly auth cookie automatically.
 * - BUG-004/005: Authorization header is no longer injected from localStorage; auth is cookie-only.
 */
const instance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

const stripeApi = axios.create({
  baseURL: STRIPE_API_BASE_URL,
  withCredentials: true,
});

const isAbsoluteOrSpecialUrl = (value) =>
  typeof value === 'string' &&
  (/^(?:[a-z][a-z\d+\-.]*:|\/\/)/i.test(value) || value.startsWith('data:'));

const normalizeRequestUrl = (url, baseURL) => {
  if (typeof url !== 'string' || !url) return url;
  if (isAbsoluteOrSpecialUrl(url)) return url;

  const normalizedBase = normalizeApiBase(baseURL);
  if (!normalizedBase) return url;

  if (url === '/api' || url === 'api') {
    return '';
  }

  if (url.startsWith('/api/')) {
    return url.slice('/api/'.length);
  }

  if (url.startsWith('api/')) {
    return url.slice('api/'.length);
  }

  return url;
};

// Match the effective destination, including absolute URLs and relative traversal.
// Sharing an Axios client must not share application credentials with other URLs.
const getApiRequestPath = (config = {}) => {
  try {
    const pageUrl = typeof window !== 'undefined'
      ? window.location.href : API_BASE_URL;
    const base = config.baseURL || API_BASE_URL;
    const url = normalizeRequestUrl(config.url || '', base);
    const destination = new URL(isAbsoluteOrSpecialUrl(url)
      ? url : `${base.replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}`, pageUrl);
    for (const allowedBase of [API_BASE_URL, STRIPE_API_BASE_URL]) {
      const allowed = new URL(allowedBase, pageUrl);
      if (destination.origin === allowed.origin &&
          destination.pathname.startsWith(allowed.pathname)) {
        return destination.pathname.slice(allowed.pathname.length);
      }
    }
  } catch (_) {
    // An unresolvable destination cannot own application authentication.
  }
  return null;
};

const attachRequestHeaders = (config = {}) => {
  try {
    config.headers = config.headers || {};
    config.url = normalizeRequestUrl(config.url, config.baseURL || API_BASE_URL);
    if (getApiRequestPath(config) === null) {
      config.withCredentials = false;
      return config;
    }

    // BUG-004/005: Auth token is normally in httpOnly cookie - sent automatically.
    // Fallback: if a legacy JWT still exists in localStorage, attach it as Bearer.
    const token = localStorage.getItem('token');
    const hasAuthorization = typeof config.headers.has === 'function'
      ? config.headers.has('Authorization')
      : Object.keys(config.headers).some((name) => name.toLowerCase() === 'authorization');
    if (isLikelyJwtToken(token) && !hasAuthorization) {
      config.headers.Authorization = 'Bearer ' + token;
    }

    // Attach CSRF token from cookie for double-submit cookie pattern (BUG-013)
    const csrfToken = readCsrfToken();
    if (csrfToken) {
      config.headers['x-csrf-token'] = csrfToken;
    }

    // Forward currently selected UI language so backend can localize entity text fields.
    const uiLang =
      localStorage.getItem('speakable-language') ||
      localStorage.getItem('i18nextLng');
    if (uiLang) {
      config.headers['x-ui-language'] = String(uiLang)
        .toLowerCase()
        .split('-')[0]
        .split('_')[0];
    }
  } catch (error) {
    // Handle cases where localStorage is not available
    console.warn('localStorage not available:', error);
  }
  return config;
};

instance.interceptors.request.use(
  attachRequestHeaders,
  error => {
    return Promise.reject(error);
  }
);

stripeApi.interceptors.request.use(
  attachRequestHeaders,
  error => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor: redirect to login on 401 (expired/invalid token).
 *
 * Skips the redirect for auth endpoints (login, signup, signout) so that
 * their own error handling can run without interference.
 *
 * Legacy token refresh support is opt-in only. The current backend auth stack
 * is cookie-based and does not expose `/api/auth/refresh`, so a blind refresh
 * attempt would create extra 404 noise before redirecting to login.
 */

const isAuthEndpoint = (url) =>
  /(?:^|\/)(?:login|signin(?:[_/-]|$)|signup|signout|auth\/refresh)/i.test(String(url ?? ''));

const isBootstrapSessionRequest = (url) =>
  /^\/?me(?:[/?#]|$)/i.test(String(url ?? ''));

const ENABLE_AUTH_REFRESH =
  String(process.env.REACT_APP_ENABLE_AUTH_REFRESH || '').toLowerCase() === 'true';

// Non-critical endpoints — 401 here should NOT force logout/redirect.
// These are background requests that may fire before auth is fully established.
const isNonCritical401 = (url) => {
  const soft = [
    'chat/greeting', 'public-chat/', 'notifications/inbox-config',
    'notifications/preferences', 'wallet/balance', 'auth/refresh',
    'v1/admin/voice-leads',
  ];
  const u = String(url || '').toLowerCase();
  return soft.some((s) => u.includes(s));
};

const buildLoginRedirectUrl = () => {
  const loginPath = getLoginRedirectPath();
  if (typeof window === 'undefined') return loginPath;

  const currentPath = [
    window.location.pathname || '/',
    window.location.search || '',
    window.location.hash || '',
  ].join('');

  if (!currentPath || currentPath === '/' || currentPath.startsWith(loginPath)) {
    return loginPath;
  }

  const separator = loginPath.includes('?') ? '&' : '?';
  return `${loginPath}${separator}redirect=${encodeURIComponent(currentPath)}`;
};

const create401Handler = (client) => {
  let refreshPromise = null;
  return async (error) => {
  const requestUrl = getApiRequestPath(error.config);
  if (requestUrl === null) return Promise.reject(error);
  const currentPathname = typeof window !== 'undefined' ? String(window.location.pathname || '') : '';
  const isCounsellorRequest =
    /\/?v1\/counsellor\//i.test(requestUrl) ||
    currentPathname.startsWith('/Counsellor-Dashboard');

  if (
    error.response?.status === 401 &&
    !isAuthEndpoint(requestUrl)
  ) {
    if (error.config?.skipAuthRedirect === true) {
      return Promise.reject(error);
    }

    // Non-critical background requests: just reject silently, do NOT redirect.
    if (isNonCritical401(requestUrl)) {
      return Promise.reject(error);
    }

    if (isBootstrapSessionRequest(requestUrl)) {
      clearAuthStorage();
      return Promise.reject(error);
    }

    if (ENABLE_AUTH_REFRESH && !error.config?._authRefreshRetried) {
      error.config._authRefreshRetried = true;
      try {
        if (!refreshPromise) {
          refreshPromise = client.post('auth/refresh').finally(() => {
            refreshPromise = null;
          });
        }
        await refreshPromise;
        return client(error.config);
      } catch (_refreshErr) {
        // All callers awaiting the shared refresh settle through this failure path.
      }
    }

    clearAuthStorage();
    // --- Existing logout logic (unchanged) ---
    if (isCounsellorRequest) {
      if (typeof window !== 'undefined' && window.location.pathname !== '/counsellor-login') {
        window.location.assign('/counsellor-login');
      }
      return Promise.reject(error);
    }

    // Legacy fallback for the rest of the app (BUG-033).
    const loginPath = getLoginRedirectPath();
    if (typeof window !== 'undefined' && window.location.pathname !== loginPath) {
      window.location.assign(buildLoginRedirectUrl());
    }
  }
  return Promise.reject(error);
  };
};

instance.interceptors.response.use(undefined, create401Handler(instance));
stripeApi.interceptors.response.use(undefined, create401Handler(stripeApi));

export { API_BASE_URL, STRIPE_API_BASE_URL, stripeApi };
export default instance;
